import api from './api';
import { WorkflowAssignment, TaskProgress, PaginatedResponse, AssignmentStatus, WorkflowWidgetData } from '../types';

export interface CreateAssignmentRequest {
  workflowId: string;
  workerId: string;
  assetId?: string;  // Optional - for asset-agnostic workflows
  dueDate?: string;
  eventDate?: string; // Specific event date (when workflow event_category is 'specific_date')
  // Widget data for widgets with workflow data source
  widgetData?: WorkflowWidgetData;
}

export const assignmentService = {
  getAssignments: async (page = 1, pageSize = 10, filters?: { workflowId?: string; assetId?: string; statusFilter?: string; includeEventPrices?: boolean }): Promise<PaginatedResponse<WorkflowAssignment>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (filters?.workflowId) params.workflow_id = filters.workflowId;
    if (filters?.assetId) params.asset_id = filters.assetId;
    if (filters?.statusFilter) params.status_filter = filters.statusFilter;
    if (filters?.includeEventPrices) params.include_event_prices = true;
    const response = await api.get<PaginatedResponse<WorkflowAssignment>>('/assignments', { params });
    return response.data;
  },

  getAssignment: async (id: string): Promise<WorkflowAssignment> => {
    const response = await api.get<WorkflowAssignment>(`/assignments/${id}`);
    return response.data;
  },

  createAssignment: async (data: CreateAssignmentRequest): Promise<WorkflowAssignment> => {
    const response = await api.post<WorkflowAssignment>('/assignments', data);
    return response.data;
  },

  updateAssignmentStatus: async (id: string, status: AssignmentStatus): Promise<WorkflowAssignment> => {
    const response = await api.put<WorkflowAssignment>(`/assignments/${id}/status`, { status });
    return response.data;
  },

  deleteAssignment: async (id: string): Promise<void> => {
    await api.delete(`/assignments/${id}`);
  },

  updateAssignment: async (
    id: string,
    data: { workerId?: string; dueDate?: string; eventDate?: string }
  ): Promise<WorkflowAssignment> => {
    const response = await api.put<WorkflowAssignment>(`/assignments/${id}`, data);
    return response.data;
  },

  getProgress: async (assignmentId: string): Promise<TaskProgress[]> => {
    const response = await api.get<TaskProgress[]>(`/progress/${assignmentId}`);
    return response.data;
  },

  updateStepProgress: async (
    assignmentId: string,
    stepId: string,
    data: Partial<TaskProgress>
  ): Promise<TaskProgress> => {
    const response = await api.put<TaskProgress>(
      `/progress/${assignmentId}/step/${stepId}`,
      data
    );
    return response.data;
  },

  completeStep: async (
    assignmentId: string,
    stepId: string,
    data?: Record<string, unknown>
  ): Promise<TaskProgress> => {
    const response = await api.post<TaskProgress>(
      `/progress/${assignmentId}/step/${stepId}/complete`,
      { data }
    );
    return response.data;
  },

  reopenStep: async (
    assignmentId: string,
    stepId: string
  ): Promise<TaskProgress> => {
    const response = await api.post<TaskProgress>(
      `/progress/${assignmentId}/step/${stepId}/reopen`
    );
    return response.data;
  },
};
